import React, { useEffect } from 'react';

import { Container, Pagination } from '@mui/material';
import { observer } from 'mobx-react';
import { FormProvider, useForm } from 'react-hook-form';

import classes from './NgzEntityList.module.scss';
import { angularizeWithProviders } from '../../../shared/providers';
import { TitleRow } from './TitleRow/TitleRow';
import { FilterRow } from './FilterRow/FilterRow';
import { entityListStore } from '../../stores/entityListStore';
import { EntityRow } from './EntityRow/EntityRow';
import { EntityListPageTitle } from './EntityListPageTitle/EntityListPageTitle';
import { EntityListFormValues } from '../../interfaces/entity-list/EntityListFilters';
import { useAutoSubmitForm } from '../../../shared/hooks/useAutoSubmitForm';
import { EmptyList } from '../../../shared/components/ui-components';
import { ListMode, filterDefaultValues } from './NgzEntityList.settings';

type NgzEntityListProps = {
  listMode: ListMode;
};

/*
INFORMATION ABOUT THE COMPONENT:
The component displays a list of entities; 
Filtering occurs automatically when data is entered into the filter; 
Tabs are part of filters (filtering by payment type and solutions); 
Selected filters must be saved when switching between tabs; 
Clearing filters (the clear button) clears all filters except for the selected tab; 
(Don't forget to check reset logic when Tabs updated)
Do not save used filters after unmounting.

FILTERING LOGIC:
The filtering system operates in two distinct modes (listMode) based on solution types:

Add solutionModeType because can't filtered by solutions in All case, need show NUTRITION and NUTRITION & SCAN_IQ entities.
Solutions filter is deep equal filter, show only if all values are the same.
   
1. NUTRITION MODE:
   - Shows entities with contains solution: NUTRITION
   - Filters by payment flow type (ALL, NEW_USER_FLOW_2024, NEW, OLD)
   - Requires solutionModeType: NUTRITION to filter out SCAN_IQ entities 

2. SCAN_IQ MODE:
   - Shows entities with contains solution: SCAN_IQ (only NEW_USER_FLOW_2024 flow)
   - Filters by solutions  (ALL, SCAN_IQ & NUTRITION, SCAN_IQ) (plus hidden CHALLENGE)
   - Requires solutionModeType: SCAN_IQ to filter out entities only with NUTRITION in All case
*/

const NgzEntityList = observer(({ listMode }: NgzEntityListProps) => {
  const { entityList, filterMethods, setDefaultValues } = entityListStore;
  const {
    isShowPlaceholder,
    isSearchUsed,
    isShowPagination,
    getPaginationProps,
    applyFilters,
    resetFilterData,
  } = filterMethods;

  const formMethods = useForm<EntityListFormValues>();
  const { handleSubmit, watch, reset } = formMethods;

  useAutoSubmitForm(handleSubmit(applyFilters), watch, [
    'businessName',
    'ownerName',
    'businessPhone',
  ]);

  useEffect(() => {
    if (!listMode) {
      throw new Error('listMode is required');
    }

    const defaultValues = filterDefaultValues[listMode];

    resetFilterData(defaultValues); // reset filter data to omit set isSearchUsed to true
    reset(defaultValues); // trigger useAutoSubmitForm and get initial data, use getEntityList if delete

    return () => setDefaultValues();
  }, [listMode]);

  const paginationProps = getPaginationProps();

  return (
    <Container>
      <FormProvider {...formMethods}>
        <EntityListPageTitle listMode={listMode} />
        <div className={classes.entityTable}>
          <TitleRow listMode={listMode} />
          <FilterRow listMode={listMode} />
          {entityList.map((entityInfo) => (
            <EntityRow
              key={entityInfo.entityId}
              entityInfo={entityInfo}
              listMode={listMode}
            />
          ))}
        </div>
      </FormProvider>
      <EmptyList
        show={isShowPlaceholder}
        isSearch={isSearchUsed}
        description="There are no entities"
      />
      {isShowPagination && <Pagination {...paginationProps} />}
    </Container>
  );
});

angularizeWithProviders(NgzEntityList, 'ngzEntityList', {
  listMode: '@',
});

export { NgzEntityList };
